if (!customElements.get('quick-add-modal')) {
  customElements.define(
    'quick-add-modal',
    class QuickAddModal extends ModalDialog {
      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');

        this.addEventListener('product-info:loaded', ({ target }) => {
          target.addPreProcessCallback(this.preprocessHTML.bind(this));
        });
      }

      hide(preventFocus = false) {
        const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (cartNotification) cartNotification.setActiveElement(this.openedBy);
        this.modalContent.innerHTML = '';

        if (preventFocus) this.openedBy = null;
        super.hide();
      }

      show(opener) {
        opener.setAttribute('aria-disabled', true);
        opener.classList.add('loading');
        opener.querySelector('.loading__spinner').classList.remove('hidden');

        fetch(opener.getAttribute('data-product-url'))
          .then((response) => response.text())
          .then((responseText) => {
            const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
            const productElement = responseHTML.querySelector('product-info');

            this.preprocessHTML(productElement);
            HTMLUpdateUtility.setInnerHTML(this.modalContent, productElement.outerHTML);

            if (window.Shopify && Shopify.PaymentButton) {
              Shopify.PaymentButton.init();
            }
            if (window.ProductModel) window.ProductModel.loadShopifyXR();

            // 🔑 Re-init swatch filtering inside modal
            this.initColorSwatchFilters();

            super.show(opener);
          })
          .finally(() => {
            opener.removeAttribute('aria-disabled');
            opener.classList.remove('loading');
            opener.querySelector('.loading__spinner').classList.add('hidden');
          });
      }

      preprocessHTML(productElement) {
        productElement.classList.forEach((classApplied) => {
          if (classApplied.startsWith('color-') || classApplied === 'gradient')
            this.modalContent.classList.add(classApplied);
        });
        this.preventDuplicatedIDs(productElement);
        this.removeDOMElements(productElement);
        this.removeGalleryListSemantic(productElement);
        this.updateImageSizes(productElement);
        this.preventVariantURLSwitching(productElement);
      }

      preventVariantURLSwitching(productElement) {
        productElement.setAttribute('data-update-url', 'false');
      }

      removeDOMElements(productElement) {
        const pickupAvailability = productElement.querySelector('pickup-availability');
        if (pickupAvailability) pickupAvailability.remove();

        const productModal = productElement.querySelector('product-modal');
        if (productModal) productModal.remove();

        const modalDialog = productElement.querySelectorAll('modal-dialog');
        if (modalDialog) modalDialog.forEach((modal) => modal.remove());
      }

      preventDuplicatedIDs(productElement) {
        const sectionId = productElement.dataset.section;

        const oldId = sectionId;
        const newId = `quickadd-${sectionId}`;
        productElement.innerHTML = productElement.innerHTML.replaceAll(oldId, newId);
        Array.from(productElement.attributes).forEach((attribute) => {
          if (attribute.value.includes(oldId)) {
            productElement.setAttribute(attribute.name, attribute.value.replace(oldId, newId));
          }
        });

        productElement.dataset.originalSection = sectionId;
      }

      removeGalleryListSemantic(productElement) {
        const galleryList = productElement.querySelector('[id^="Slider-Gallery"]');
        if (!galleryList) return;

        galleryList.setAttribute('role', 'presentation');
        galleryList.querySelectorAll('[id^="Slide-"]').forEach((li) => li.setAttribute('role', 'presentation'));
      }

      updateImageSizes(productElement) {
        const product = productElement.querySelector('.product');
        const desktopColumns = product?.classList.contains('product--columns');
        if (!desktopColumns) return;

        const mediaImages = product.querySelectorAll('.product__media img');
        if (!mediaImages.length) return;

        let mediaImageSizes =
          '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';

        if (product.classList.contains('product--medium')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '605px');
        } else if (product.classList.contains('product--small')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '495px');
        }

        mediaImages.forEach((img) => img.setAttribute('sizes', mediaImageSizes));
      }

      // 🔥 New function for color swatch filtering
      initColorSwatchFilters() {
        this.modalContent.querySelectorAll(".for_cs_swatch").forEach((section) => {
          const filterImages = () => {
            const colorFieldset = section.querySelector(".opt_name_color");
            const seriesFieldset = section.querySelector(".opt_name_series");

            const allLis = Array.from(section.querySelectorAll("li[data-vname]"));

            const colorInput = colorFieldset?.querySelector("input:checked");
            const seriesInput = seriesFieldset?.querySelector("input:checked");
            const colorValue = colorInput?.value?.trim() ?? "";
            const seriesValue = seriesInput?.value?.trim() ?? "";

            allLis.forEach((li) => {
              const v = (li.dataset.vname ?? "").trim();
              const shouldShow =
                v === "" ||
                (colorValue && v === colorValue) ||
                (seriesValue && v === seriesValue);

              li.classList.toggle("hide_swatch_var", !shouldShow);
            });
          };

          filterImages();

          const scrollThumbListToTopForSection = (section) => {
            const MAX_RETRIES = 12;
            let attempts = 0;

            const findAndScroll = () => {
              attempts++;
              const lists = Array.from(
                document.querySelectorAll(
                  ".product__media-wrapper.main__pdp__grid .thumbnail-slider .thumbnail-list"
                )
              );

              if (lists.length === 0) {
                if (attempts < MAX_RETRIES) {
                  setTimeout(findAndScroll, 100);
                }
                return;
              }

              let targetList = lists[0];
              if (lists.length > 1 && section && section.getBoundingClientRect) {
                const secTop = section.getBoundingClientRect().top;
                let minDist = Infinity;
                lists.forEach((l) => {
                  const rect = l.getBoundingClientRect();
                  const dist = Math.abs(rect.top - secTop);
                  if (dist < minDist) {
                    minDist = dist;
                    targetList = l;
                  }
                });
              }

              const origScrollBehavior = targetList.style.scrollBehavior || "";
              const origSnapType = targetList.style.scrollSnapType || "";

              try {
                targetList.style.scrollBehavior = "auto";
                targetList.style.scrollSnapType = "none";

                try { targetList.scrollTop = 0; } catch (e) {}
                try { targetList.scrollTo({ top: 0, left: 0, behavior: "auto" }); } catch (e) {}
              } finally {
                setTimeout(() => {
                  targetList.style.scrollBehavior = origScrollBehavior;
                  targetList.style.scrollSnapType = origSnapType;
                }, 120);
              }

              const mo = new MutationObserver((mutations, obs) => {
                targetList.style.scrollBehavior = "auto";
                targetList.style.scrollSnapType = "none";
                try { targetList.scrollTop = 0; } catch (e) {}
                try { targetList.scrollTo({ top: 0, left: 0, behavior: "auto" }); } catch (e) {}
                setTimeout(() => {
                  targetList.style.scrollBehavior = origScrollBehavior;
                  targetList.style.scrollSnapType = origSnapType;
                }, 80);
                obs.disconnect();
              });

              mo.observe(targetList, { childList: true, subtree: true });
            };

            setTimeout(findAndScroll, 50);
          };

          section.addEventListener("change", (e) => {
            if (
              e.target.matches("input") &&
              (e.target.closest(".opt_name_color") || e.target.closest(".opt_name_series"))
            ) {
              filterImages();
              scrollThumbListToTopForSection(section);
            }
          });

          const mo = new MutationObserver(() => {
            filterImages();
          });
          mo.observe(section, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ["data-vname", "class"]
          });
        });
      }
    }
  );
}
